import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/ApiService';
import Header from './Header';
import { FiUser, FiLock, FiUsers } from 'react-icons/fi';
import AddCollaborators from './AddCollaborators';
import '../styles/Profile.css';
import { Button, TextField, Snackbar, Alert } from '@mui/material';

const Profile = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  
  // Debug para verificar o tipoPlano
  
  // Verificar dados armazenados no localStorage
  const storedUserData = localStorage.getItem('user_data');
  if (storedUserData) {
    try {
      const userData = JSON.parse(storedUserData);
    } catch (e) {
      
    }
  } else {
    
  }
  
  const { darkMode } = useTheme();
  
  // Estados
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('perfil');
  
  // Estados para alteração de senha
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [senhaError, setSenhaError] = useState('');
  const [senhaSuccess, setSenhaSuccess] = useState(false);
  
  // Verificar se o usuário pode gerenciar colaboradores
  let tipoPlano = currentUser?.tipoPlano;
  
  // Se o tipoPlano não estiver definido no currentUser, tentar buscá-lo do localStorage
  if (!tipoPlano) {
    // Verificar em várias fontes possíveis para encontrar o tipoPlano
    try {
      // 1. Verificar no localStorage
      const storedUserData = localStorage.getItem('user_data');
      if (storedUserData) {
        const userData = JSON.parse(storedUserData);
        if (userData && userData.tipoPlano) {
          tipoPlano = userData.tipoPlano;
        }
      }
      
      // 2. Verificar nos dados de usuário offline
      if (!tipoPlano && currentUser?.username) {
        const offlineUserData = localStorage.getItem(`offline_user_${currentUser.username}`);
        if (offlineUserData) {
          const parsedData = JSON.parse(offlineUserData);
          if (parsedData.tipoPlano) {
            tipoPlano = parsedData.tipoPlano;
          } else if (parsedData.user && parsedData.user.tipoPlano) {
            tipoPlano = parsedData.user.tipoPlano;
          }
        }
      }
    } catch (e) {
      
    }
  }
  
  const canManageCollaborators = tipoPlano === "PLANO MENSAL CORPORATIVO";
  
  // Carregar dados do usuário
  useEffect(() => {
    const loadUserData = async () => {
      try {
        if (currentUser) {
          setUserData(currentUser);
        }
      } catch (error) {
        
        setError('Erro ao carregar dados do perfil');
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [currentUser]);

  // Handler para alterar senha
  const handleAlterarSenha = async (e) => {
    e.preventDefault();
    setSenhaError('');
    setSenhaSuccess(false);

    // Validações
    if (!senhaAtual || !novaSenha || !confirmarSenha) {
      setSenhaError('Todos os campos são obrigatórios');
      return;
    }

    if (novaSenha !== confirmarSenha) {
      setSenhaError('As senhas não coincidem');
      return;
    }

    if (novaSenha.length < 6) {
      setSenhaError('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    try {
      setLoading(true);
      const result = await apiService.alterarSenha({
        senhaAtual,
        novaSenha
      });

      if (result.success) {
        setSenhaSuccess(true);
        setSenhaAtual('');
        setNovaSenha('');
        setConfirmarSenha('');
      } else {
        setSenhaError(result.error || 'Erro ao alterar senha');
      }
    } catch (error) {
      setSenhaError('Erro ao processar a alteração de senha');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !userData) {
    return <div className="loading">Carregando...</div>;
  }

  return (
    <div className={`profile-container ${darkMode ? 'dark-mode' : ''}`}>
      <Header />
      
      <main className="profile-content">
        <div className="profile-header">
          <h1>Meu Perfil</h1>
        </div>

        <div className="profile-grid profile-grid-single">
          {/* Dados e Configurações */}
          <div className="profile-details-section">
            <div className="profile-tabs">
              <button 
                className={`tab-button ${activeTab === 'perfil' ? 'active' : ''}`}
                onClick={() => setActiveTab('perfil')}
              >
                <FiUser /> Dados do Perfil
              </button>
              <button 
                className={`tab-button ${activeTab === 'senha' ? 'active' : ''}`}
                onClick={() => setActiveTab('senha')}
              >
                <FiLock /> Alterar Senha
              </button>
              {canManageCollaborators && (
                <button 
                  className={`tab-button ${activeTab === 'colaboradores' ? 'active' : ''}`}
                  onClick={() => setActiveTab('colaboradores')}
                >
                  <FiUsers /> Gerenciar Colaboradores
                </button>
              )}
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {(() => {
              switch (activeTab) {
                case 'perfil':
                  return (
                    <div className="profile-info">
                      <div className="info-group">
                        <label>Nome</label>
                        <p>{userData?.nome || userData?.username || 'Não informado'}</p>
                      </div>
                      <div className="info-group">
                        <label>Email</label>
                        <p>{userData?.email || 'Não informado'}</p>
                      </div>
                      <div className="info-group">
                        <label>Empresa</label>
                        <p>{userData?.empresa || 'Não informado'}</p>
                      </div>
                      <div className="info-group">
                        <label>Último Acesso</label>
                        <p>{userData?.ultimo_acesso ? new Date(userData.ultimo_acesso).toLocaleString() : 'Não registrado'}</p>
                      </div>
                    </div>
                  );
                case 'senha':
                  return (
                    <div className="alterar-senha-form">
                      <form onSubmit={handleAlterarSenha}>
                        <TextField
                          fullWidth
                          label="Senha Atual"
                          type="password"
                          value={senhaAtual}
                          onChange={(e) => setSenhaAtual(e.target.value)}
                          margin="normal"
                          required
                        />
                        <TextField
                          fullWidth
                          label="Nova Senha"
                          type="password"
                          value={novaSenha}
                          onChange={(e) => setNovaSenha(e.target.value)}
                          margin="normal"
                          required
                        />
                        <TextField
                          fullWidth
                          label="Confirmar Nova Senha"
                          type="password"
                          value={confirmarSenha}
                          onChange={(e) => setConfirmarSenha(e.target.value)}
                          margin="normal"
                          required
                        />
                        {senhaError && (
                          <div className="error-message">{senhaError}</div>
                        )}
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          fullWidth
                          sx={{ mt: 2 }}
                          disabled={loading}
                        >
                          Alterar Senha
                        </Button>
                      </form>
                    </div>
                  );
                case 'colaboradores':
                  return canManageCollaborators ? (
                    <div className="collaborators-section">
                      <AddCollaborators />
                    </div>
                  ) : null;
                default:
                  return null;
              }
            })()}
          </div>
        </div>
      </main>

      <Snackbar
        open={senhaSuccess}
        autoHideDuration={6000}
        onClose={() => setSenhaSuccess(false)}
      >
        <Alert severity="success" onClose={() => setSenhaSuccess(false)}>
          Senha alterada com sucesso!
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Profile; 